import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiDollarSign, FiUsers, FiVideo, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat, safeToFixed } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  Card,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from './Analytics.styles';

const Overview = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getOverview(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
      // Don't show error toast for development
      // showToast('error', 'Failed to load platform overview');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'KES 0';
    return `KES ${parseFloat(amount).toLocaleString('en-KE')}`;
  };

  const prepareRevenueTrend = () => {
    if (!data?.revenue?.monthly_trend) return [];
    return data.revenue.monthly_trend.map((item) => ({
      month: item.month.split('-')[1], // Extract month number
      amount: safeParseFloat(item.amount),
    }));
  };

  const prepareRoleDistribution = () => {
    if (!data?.users) return [];
    const roles = data.users;
    return [
      { name: 'Students', value: roles.students || 0 },
      { name: 'Teachers', value: roles.teachers || 0 },
      { name: 'Parents', value: roles.parents || 0 },
    ];
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading platform overview..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiAlertCircle size={64} />
        <h3>No Data Available</h3>
        <p>Platform analytics will appear once data is available</p>
      </EmptyState>
    );
  }

  const COLORS = [theme.colors.primary, theme.colors.secondary, theme.colors.accent];
  const roleData = prepareRoleDistribution();
  const revenueData = prepareRevenueTrend();

  return (
    <>
      {/* Key Metrics */}
      <Section>
        <SectionTitle>
          <FiCheckCircle />
          Platform Overview
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Total Users</StatLabel>
            <StatValue>{safeParseFloat(data.users?.total).toLocaleString()}</StatValue>
            <StatDescription>
              <Badge color={theme.colors.success}>
                +{safeParseFloat(data.users?.new_last_days).toLocaleString()} new
              </Badge>
              new users
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Total Revenue</StatLabel>
            <StatValue>{formatCurrency(data.revenue?.total)}</StatValue>
            <StatDescription>All-time platform revenue</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Active Students</StatLabel>
            <StatValue>{safeParseFloat(data.users?.active_students_last_days).toLocaleString()}</StatValue>
            <StatDescription>Active students</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Total Courses</StatLabel>
            <StatValue>{safeParseFloat(data.content?.courses).toLocaleString()}</StatValue>
            <StatDescription>
              {safeParseFloat(data.content?.videos).toLocaleString()} videos
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Revenue Trend */}
      {revenueData.length > 0 && (
        <Section>
          <SectionTitle>
            <FiTrendingUp />
            Revenue Trend
          </SectionTitle>
          <Card>
            <ChartContainer height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="month" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke={theme.colors.primary} 
                    strokeWidth={2}
                    dot={{ fill: theme.colors.primary }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}

      {/* User Distribution */}
      {roleData.some(r => r.value > 0) && (
        <Section>
          <SectionTitle>
            <FiUsers />
            User Distribution
          </SectionTitle>
          <Card>
            <ChartContainer height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={roleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}
    </>
  );
};

export default Overview;

